import React from 'react';
import type { ReactNode } from 'react';
import type { StyleProp, ViewStyle } from 'react-native';
import { StyleSheet, View } from './rn';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { spacing } from '../theme';
import { useTheme } from '../theme/ThemeContext';
import { useThemeMode } from '../theme/ThemeContext';
import { getGlassView } from './nativeGlass';
import HeaderBackButton from './HeaderBackButton';

type Props = {
  children: ReactNode;
  contentStyle?: StyleProp<ViewStyle>;
  /**
   * Başlığı içeriğin ÜSTÜNDE yüzdürür (7.14 · D-550).
   *
   * İstenerek açılıyor: akıştaki başlık bugün on iki ekranda kullanılıyor ve
   * hepsini birden çevirmek, her birinin kaydırma boşluğunu aynı anda
   * değiştirmek demek. Yüzen başlık kullanan ekran, aynı yüksekliği
   * `screenHeaderContentInset` ile tepede geri vermek zorunda — yoksa ilk
   * satır başlığın altında kalır.
   */
  floating?: boolean;
};

// Bütün üst-seviye mobil sayfalarda geri kontrolünün optik başlangıç noktası:
// ekranın solundan 16 px, güvenli alanın altından 8 px. İçerik yüksekliği 56 px
// olduğundan 40x40 geri kontrolü tam olarak aynı koordinata oturur.
export default function ScreenHeader({ children, contentStyle, floating }: Props) {
  const insets = useSafeAreaInsets();
  const colors = useTheme();
  const { isDark } = useThemeMode();
  const GlassView = React.useMemo(() => getGlassView(), []);

  /**
   * Yüzen başlık BANDIN TAMAMINDA yüzey taşımak zorunda (7.14 · D-550).
   *
   * İlk denemede yalnız geri düğmesinin yuvarlağı yüzeydi, başlık yazısı
   * çıplaktı — altından geçen liste satırı yazının üstüne bindi ve ikisi de
   * okunmaz oldu. Dock'ta bu olmadı çünkü dock baştan sona bir yüzey. İçeriğin
   * altından geçebilmesi, üstünde durduğu şeyin sürekli olmasına bağlı.
   */
  const yuzey = floating ? (
    GlassView ? (
      <GlassView
        style={styles.surface}
        glassEffectStyle="regular"
        colorScheme={isDark ? 'dark' : 'light'}
        pointerEvents="none"
      />
    ) : (
      /* Malzeme yoksa bant donuk kalır: yarı saydam bir yedek, altından geçen
         metni okunur kılmaz — cam olmayan yerde içerik gizlenmeli. */
      <View style={[styles.surface, { backgroundColor: colors.white }]} pointerEvents="none" />
    )
  ) : null;

  return (
    <View
      testID="screen-header-safe"
      pointerEvents={floating ? 'box-none' : undefined}
      style={[{ paddingTop: insets.top }, floating && styles.floating]}>
      {yuzey}
      <View testID="screen-header-content" style={[styles.content, contentStyle]}>{children}</View>
    </View>
  );
}

export function ScreenBackButton({ onPress }: { onPress: () => void }) {
  return (
    <View style={styles.overlay} pointerEvents="box-none">
      <ScreenHeader>
        <HeaderBackButton onPress={onPress} />
      </ScreenHeader>
    </View>
  );
}

const styles = StyleSheet.create({
  content: {
    height: 56,
    paddingHorizontal: spacing.md,
    flexDirection: 'row',
    alignItems: 'center',
  },
  // Bandın tamamını kaplar: güvenli alan dahil, içerik satırının altına kadar.
  surface: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  // Yüzen başlık: içerik altından geçer, dokunma yalnız kontrollere iner.
  floating: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 10,
  },
  overlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: 72,
    zIndex: 1000,
  },
});
